/*
 * RemoteAccessPanel.js
 * --------------------
 * Provides an interactive SSH shell to edge devices directly from the
 * browser using xterm.js and a WebSocket-to-SSH relay on the server.
 *
 * Protocol:
 *   1. User fills in a user name and password and clicks Connect. Password
 *      only: a private key pasted into a browser page travels the whole proxy
 *      chain, so the console never asks for one.
 *   2. A WebSocket is opened to /node/{nodeId}/ssh via the reverse proxy.
 *   3. Credentials are sent as the first JSON message.
 *   4. The server bridges WebSocket ↔ SSH PTY.
 *   5. xterm.js renders the remote shell in the browser.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { FiTerminal, FiX, FiLock, FiUser } from "react-icons/fi";

// ── Theme matching Weavian dark sidebar ──────────────────────────────────────

const TERM_THEME = {
  background: "#0f172a",
  foreground: "#e2e8f0",
  cursor: "#818cf8",
  cursorAccent: "#0f172a",
  selectionBackground: "rgba(99, 102, 241, 0.35)",
  selectionForeground: "#f8fafc",
  black: "#1e293b",
  red: "#ef4444",
  green: "#22c55e",
  yellow: "#f59e0b",
  blue: "#6366f1",
  magenta: "#a855f7",
  cyan: "#22d3ee",
  white: "#e2e8f0",
  brightBlack: "#475569",
  brightRed: "#f87171",
  brightGreen: "#4ade80",
  brightYellow: "#fbbf24",
  brightBlue: "#818cf8",
  brightMagenta: "#c084fc",
  brightCyan: "#67e8f9",
  brightWhite: "#f8fafc",
};

// The terminal's greeting: its title inside a frame this many columns wide.
const GREETING = "Weavian Remote Access Terminal";
const GREETING_W = 42;

export default function RemoteAccessPanel({ selectedNode, singleEdge }) {
  const termContainerRef = useRef(null);
  const terminalRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);
  const inputDisposableRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");

  // SSH form state
  const [username, setUsername] = useState("root");
  const [password, setPassword] = useState("");
  const [port, setPort] = useState(22);

  // ── Initialize xterm.js ────────────────────────────────────────────────

  useEffect(() => {
    if (!termContainerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: "block",
      fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", "Menlo", monospace',
      fontSize: 14,
      lineHeight: 1.35,
      letterSpacing: 0,
      theme: TERM_THEME,
      allowProposedApi: true,
      scrollback: 5000,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(termContainerRef.current);

    // Small delay to ensure the container has dimensions before fitting
    requestAnimationFrame(() => {
      try { fitAddon.fit(); } catch (_) { /* noop */ }
    });

    terminalRef.current = term;
    fitAddonRef.current = fitAddon;

    term.writeln(`\x1b[1;34m╔${"═".repeat(GREETING_W)}╗`);
    term.writeln(`║   \x1b[1;37m${GREETING}\x1b[1;34m${" ".repeat(GREETING_W - 3 - GREETING.length)}║`);
    term.writeln(`╚${"═".repeat(GREETING_W)}╝\x1b[0m`);
    term.writeln("");
    term.writeln(
      "\x1b[90mSign in above to open a command line on this box.\x1b[0m"
    );
    term.writeln("");

    // Handle window resize → re-fit terminal
    const handleResize = () => {
      try { fitAddon.fit(); } catch (_) { /* noop */ }
      if (wsRef.current?.readyState === WebSocket.OPEN && term.cols && term.rows) {
        wsRef.current.send(
          JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })
        );
      }
    };
    window.addEventListener("resize", handleResize);

    // Observe container size changes (e.g. sidebar collapse)
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        try { fitAddon.fit(); } catch (_) { /* noop */ }
      });
      resizeObserver.observe(termContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      term.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  }, []); // once on mount

  // Re-fit when connected state changes (form hides → terminal grows)
  useEffect(() => {
    if (fitAddonRef.current) {
      requestAnimationFrame(() => {
        try { fitAddonRef.current.fit(); } catch (_) { /* noop */ }
      });
    }
  }, [connected]);

  // ── Connect ────────────────────────────────────────────────────────────

  const connect = useCallback(() => {
    if (!selectedNode) return;

    const term = terminalRef.current;
    if (!term) return;

    setConnecting(true);
    setError("");

    term.clear();
    term.writeln(`\x1b[33mConnecting to \x1b[1m${selectedNode}\x1b[0;33m …\x1b[0m`);
    term.writeln("");

    // Build WebSocket URL pointing at the reverse proxy
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = singleEdge
      ? `${wsProtocol}//${window.location.host}/console/api/ssh`
      : `${wsProtocol}//${window.location.host}/node/${encodeURIComponent(
          selectedNode
        )}/ssh`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      ws.send(JSON.stringify({
        username,
        password,
        port,
        cols: term.cols || 80,
        rows: term.rows || 24,
      }));
    };

    ws.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const msg = JSON.parse(event.data);
          if (msg.error) {
            setError(msg.error);
            setConnecting(false);
            term.writeln(`\r\n\x1b[31mError: ${msg.error}\x1b[0m`);
            return;
          }
          if (msg.status === "connected") {
            setConnected(true);
            setConnecting(false);
            term.clear();

            // Wire terminal keyboard input → WebSocket
            if (inputDisposableRef.current) {
              inputDisposableRef.current.dispose();
            }
            inputDisposableRef.current = term.onData((data) => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(new TextEncoder().encode(data));
              }
            });

            // Send initial size
            if (term.cols && term.rows) {
              ws.send(
                JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })
              );
            }
            return;
          }
        } catch (_) {
          // not JSON — treat as text output
          term.write(event.data);
        }
      } else {
        // Binary data from SSH stdout/stderr
        term.write(new Uint8Array(event.data));
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setConnecting(false);
      if (inputDisposableRef.current) {
        inputDisposableRef.current.dispose();
        inputDisposableRef.current = null;
      }
      term.writeln("\r\n\x1b[90m── Session closed ──\x1b[0m");
    };

    ws.onerror = () => {
      setError("WebSocket connection failed");
      setConnecting(false);
      term.writeln("\r\n\x1b[31mWebSocket connection failed.\x1b[0m");
    };
  }, [selectedNode, singleEdge, username, password, port]);

  // ── Disconnect ─────────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (inputDisposableRef.current) {
      inputDisposableRef.current.dispose();
      inputDisposableRef.current = null;
    }
    setConnected(false);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (inputDisposableRef.current) {
        inputDisposableRef.current.dispose();
      }
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  const noNode = !selectedNode;

  return (
    <div className="remote-access-root">
      {/* ── Connection toolbar ── */}
      {!connected && (
        <div className="panel ssh-connect-panel">
          <div className="panel-title">
            <FiLock size={14} style={{ marginRight: 6 }} />
            SSH Connection
          </div>
          <div className="panel-sub">
            Sign in with a user name and password on this box.
          </div>

          <div className="ssh-form-grid">
            <label className="ssh-label">
              <FiUser size={13} />
              <span>Username</span>
            </label>
            <input
              className="ssh-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="root"
              disabled={connecting}
            />

            <label className="ssh-label" htmlFor="ssh-password">
              <FiLock size={13} />
              <span>Password</span>
            </label>
            <input
              id="ssh-password"
              className="ssh-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !connecting && !noNode) connect(); }}
              placeholder="••••••••"
              disabled={connecting}
            />

            <label className="ssh-label">
              <FiTerminal size={13} />
              <span>Port</span>
            </label>
            <input
              className="ssh-input ssh-port"
              type="number"
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(parseInt(e.target.value, 10) || 22)}
              disabled={connecting}
            />
          </div>

          {error && <div className="panel-error">{error}</div>}

          <div className="ssh-actions">
            <button
              className="apply-btn ssh-connect-btn"
              onClick={connect}
              disabled={connecting || noNode}
            >
              {connecting
                ? "Connecting…"
                : noNode
                ? "Choose a box at the top right first"
                : `Connect to ${selectedNode}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Disconnect bar ── */}
      {connected && (
        <div className="ssh-toolbar">
          <div className="ssh-toolbar-info">
            <span className="ssh-status-dot online" />
            <span>
              Connected to <strong>{selectedNode}</strong> as{" "}
              <code>{username}</code>
            </span>
          </div>
          <button className="danger-btn ssh-disconnect-btn" onClick={disconnect}>
            <FiX size={14} />
            <span>Disconnect</span>
          </button>
        </div>
      )}

      {/* ── Terminal viewport ── */}
      <div
        className={`ssh-terminal-container ${connected ? "ssh-terminal-active" : ""}`}
        ref={termContainerRef}
      />
    </div>
  );
}
